import * as React from 'react';
import { CommonOptions, toast, ToastOptions } from 'react-toastify';
import ToastClose from './ToastClose';
import ExclamationCircleSVG from '../svg/ExclamationCircleSVG';
import CheckSVG from '../svg/CheckSVG';
import ExclamationCircleSVGAlternative from '../svg/ExclamationCircleSVGAlternative';
import ExclamationTriangleSVGAlternative from '../svg/ExclamationTriangleSVGAlternative';
import { WithTestIdOptional } from 'shared-client/types/test';

/**
 * Options object for calling toast on the site.
 */
interface CustomToastOptions extends WithTestIdOptional {
	content?: MessageContent;
	customContent?: JSX.Element; // Custom content in the form of JSX elements
	toastifyOptions?: ToastOptions; // Original toastify options
}

/**
 * Options for content. Includes a generic title, flexible icon, and generic message.
 */
interface MessageContent {
	title?: string;
	icon?: any;
	message?: string;
}

/**
 * Default toastify options we're setting for convenience
 */
const toastifyOptionDefaults: CommonOptions = {
	hideProgressBar: false,
	position: toast.POSITION.BOTTOM_RIGHT,
	closeButton: <ToastClose />,
};

/**
 * Default toast wrapper
 *
 * @param {CustomToastOptions} options
 */
export const toastDefault = (options: CustomToastOptions) => {
	toastBase(options);
};

/**
 * Success toast wrapper
 *
 * @param {CustomToastOptions} options
 */
export const toastSuccess = (options: CustomToastOptions) => {
	toastBase({
		content: {
			icon: <CheckSVG class="Toastify__success-icon" />,
			...options.content,
		},
		toastifyOptions: {
			type: 'success',
			...options.toastifyOptions,
		},
		customContent: options.customContent,
		testId: options.testId,
	});
};

/**
 * Info toast wrapper
 *
 * @param {CustomToastOptions} options
 */
export const toastInfo = (options: CustomToastOptions) => {
	toastBase({
		content: {
			icon: <ExclamationCircleSVGAlternative class="Toastify__info-icon" />,
			...options.content,
		},
		toastifyOptions: {
			type: 'info',
			...options.toastifyOptions,
		},
		customContent: options.customContent,
		testId: options.testId,
	});
};

/**
 * Warning toast wrapper
 *
 * @param {CustomToastOptions} options
 */
export const toastWarning = (options: CustomToastOptions) => {
	toastBase({
		content: {
			icon: <ExclamationTriangleSVGAlternative class="Toastify__warning-icon" />,
			...options.content,
		},
		toastifyOptions: {
			type: 'warning',
			...options.toastifyOptions,
		},
		customContent: options.customContent,
		testId: options.testId,
	});
};

/**
 * Error toast wrapper
 *
 * @param {CustomToastOptions} options
 */
export const toastError = (options: CustomToastOptions) => {
	toastBase({
		content: {
			icon: <ExclamationCircleSVG class="Toastify__danger-icon" />,
			...options.content,
		},
		toastifyOptions: {
			type: 'error',
			...options.toastifyOptions,
		},
		customContent: options.customContent,
		testId: options.testId,
	});
};

/**
 * Base Toast method. Uses toastify.
 *
 * @param {CustomToastOptions} options
 */
const toastBase = (options: CustomToastOptions) => {
	toast(
		<>
			{options.content ? (
				<div
					className="Toastify__wrapper"
					data-testid={options.testId ? options.testId : 'toast-message-container'}
				>
					{options.content.icon ? <span className="Toastify__toast-icon">{options.content.icon}</span> : null}
					<div className="Toastify__text">
						{options.customContent ? (
							options.customContent
						) : (
							<>
								{options.content.title ? (
									<p
										className={
											options.content.message
												? 'Toastify__title Toastify__title--bold'
												: 'Toastify__title'
										}
									>
										{options.content.title}
									</p>
								) : null}

								{options.content.message ? <p>{options.content.message}</p> : null}
							</>
						)}
					</div>
				</div>
			) : null}
		</>,
		{
			...toastifyOptionDefaults,
			...options.toastifyOptions,
		},
	);
};
